import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '@backstage/frontend-plugin-api';
import { catalogApiRef, useEntity } from '@backstage/plugin-catalog-react';
import {
  ToggleButton,
  ToggleButtonGroup,
} from '@backstage/ui';
import {
  CloudResourceEntity,
  RuleEntity,
} from '@internal/backstage-plugin-catalog-devops-common';
import { RegionChip } from '../RegionChip';
import { getConsoleUrl, isHttpUrl } from '../CloudConsoleLink';
import { useCloudResourcesContext } from '../Providers';
import {
  accountOf,
  pillClass,
  resourceNoteOf,
  resourceSeverityOf,
} from './shared';
import styles from './entity-page.module.css';

type SeverityFilter = 'all' | 'High' | 'Medium' | 'Low';

/**
 * Rule page Resources tab, 1:1 with design/rule-page-mockup.html: every
 * resource the rule affects, with its OWN severity and note
 * (spec.resources[] is per-resource — one rule can be High on one resource
 * and Low on another), the count of other failed rules touching the same
 * resource, and the console deep link. Rows navigate to the resource page.
 */
export function RuleResourcesTable() {
  const { entity } = useEntity();
  const rule = entity as RuleEntity;
  const { cloudResources } = useCloudResourcesContext();
  const catalogApi = useApi(catalogApiRef);
  const navigate = useNavigate();

  const [severity, setSeverity] = useState<SeverityFilter>('all');
  const [query, setQuery] = useState('');
  const [otherFailedByResource, setOtherFailedByResource] = useState<Map<string, number>>(
    new Map(),
  );

  const accountName = accountOf(rule);

  // Failed rules of the account, to count OTHER failed rules per resource.
  useEffect(() => {
    let cancelled = false;
    if (!accountName) {
      return () => {
        cancelled = true;
      };
    }
    catalogApi
      .getEntities({
        filter: { kind: 'Rule', 'spec.account': accountName, 'spec.result': 'Failed' },
      })
      .then(({ items }) => {
        if (cancelled) {
          return;
        }
        const counters = new Map<string, number>();
        (items as RuleEntity[]).forEach(failedRule => {
          if (failedRule.metadata.name === rule.metadata.name) {
            return;
          }
          const ids = new Set(
            (failedRule.spec.resources ?? []).map(resource => resource.id),
          );
          ids.forEach(id => {
            counters.set(id, (counters.get(id) ?? 0) + 1);
          });
        });
        setOtherFailedByResource(counters);
      })
      .catch(() => {
        // The column degrades to zeros; the table stays usable.
      });
    return () => {
      cancelled = true;
    };
  }, [catalogApi, accountName, rule.metadata.name]);

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return cloudResources
      .map(resource => {
        const name =
          resource.metadata.name || resource.metadata.title || '';
        return {
          resource,
          name,
          severity: resourceSeverityOf(rule, name),
          note: resourceNoteOf(rule, name),
          otherFailed: otherFailedByResource.get(name) ?? 0,
        };
      })
      .filter(row => {
        if (severity !== 'all' && row.severity !== severity) {
          return false;
        }
        if (q) {
          const haystack = [
            row.name,
            row.resource.metadata.title ?? '',
            row.resource.spec.service ?? '',
            row.resource.spec.region ?? '',
            row.note ?? '',
          ]
            .join(' ')
            .toLowerCase();
          if (!haystack.includes(q)) {
            return false;
          }
        }
        return true;
      });
  }, [cloudResources, rule, severity, query, otherFailedByResource]);

  const openResource = (resource: CloudResourceEntity) => {
    navigate(`/catalog/default/cloudresource/${resource.metadata.name}`);
  };

  return (
    <>
      <div className={styles.toolbar} role="toolbar" aria-label="Affected resources filters">
        <ToggleButtonGroup
          aria-label="Severity"
          selectionMode="single"
          disallowEmptySelection
          selectedKeys={new Set([severity])}
          onSelectionChange={keys => {
            const next = [...keys][0] as SeverityFilter | undefined;
            setSeverity(next ?? 'all');
          }}
        >
          <ToggleButton key="all" id="all" size="small">
            All
          </ToggleButton>
          <ToggleButton key="High" id="High" size="small">
            High
          </ToggleButton>
          <ToggleButton key="Medium" id="Medium" size="small">
            Medium
          </ToggleButton>
          <ToggleButton key="Low" id="Low" size="small">
            Low
          </ToggleButton>
        </ToggleButtonGroup>

        <label className={styles.search}>
          <span aria-hidden="true">⌕</span>
          <input
            className={styles.searchInput}
            type="search"
            placeholder="Search resources…"
            aria-label="Search affected resources"
            value={query}
            onChange={event => setQuery(event.target.value)}
          />
        </label>
        <span className={styles.resultNote}>
          {rows.length} of {cloudResources.length} shown
        </span>
      </div>

      {cloudResources.length === 0 ? (
        <div className={`${styles.card} ${styles.empty}`}>
          <div className={styles.emptyBig}>✓</div>
          <b>Nothing to fix</b>
          No resources are affected by this rule on this account.
        </div>
      ) : (
        <div className={styles.tableCard}>
          <table className={styles.table} aria-label="Resources affected by this rule">
            <thead>
              <tr>
                <th>Resource</th>
                <th>Region</th>
                <th>Service</th>
                <th>Severity</th>
                <th>Note</th>
                <th>Other failed rules</th>
                <th aria-label="Console link" />
              </tr>
            </thead>
            <tbody>
              {rows.map(row => {
                const consoleUrl = getConsoleUrl(row.resource);
                return (
                  <tr
                    key={row.resource.metadata.name}
                    className={styles.row}
                    onClick={() => openResource(row.resource)}
                  >
                    <td className={styles.resName}>{row.name}</td>
                    <td>
                      <RegionChip region={row.resource.spec.region ?? ''} />
                    </td>
                    <td>{row.resource.spec.service ?? '—'}</td>
                    <td>
                      <span className={`${styles.pill} ${pillClass(styles, row.severity)}`}>
                        <span className={styles.pillDot} />
                        {row.severity}
                      </span>
                    </td>
                    <td className={styles.muted}>{row.note ?? '—'}</td>
                    <td className={styles.num}>{row.otherFailed}</td>
                    <td>
                      {consoleUrl && isHttpUrl(consoleUrl) ? (
                        <a
                          href={consoleUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={styles.cellArr}
                          title="Open in cloud console"
                          onClick={event => event.stopPropagation()}
                        >
                          ↗
                        </a>
                      ) : (
                        <span className={styles.cellArr}>↗</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <p className={styles.cardHint} style={{ marginTop: 8 }}>
        Row click opens the resource page. The same table, with drawer preview,
        lives on the account Compliance page — this page is the deep-link
        target of its “Open full rule page ↗” action.
      </p>
    </>
  );
}
